import React, { useState } from "react";
import { TextField, Box, IconButton, Tooltip, InputAdornment } from "@material-ui/core";
import { Language } from "@material-ui/icons";
import { css } from "@emotion/css";
import { LocalizedText, TranslationLocale, PRIMARY_LOCALE, TRANSLATION_LOCALES } from "../types/common";

type TranslatedTextFieldProps = {
  label: string;
  value: LocalizedText;
  onChange: (value: LocalizedText) => void;
  multiline?: boolean;
  rows?: number;
  disabled?: boolean;
};

const TRANSLATION_KEYS = Object.keys(TRANSLATION_LOCALES) as TranslationLocale[];

const TranslatedTextField: React.VFC<TranslatedTextFieldProps> = ({
  label,
  value,
  onChange,
  multiline = false,
  rows = 1,
  disabled = false,
}) => {
  const [expanded, setExpanded] = useState(false);

  const handlePrimaryChange = (text: string) => {
    onChange({ ...value, primary: text });
  };

  const handleTranslationChange = (locale: TranslationLocale, text: string) => {
    const newTranslations = { ...value.translations, [locale]: text || undefined };
    const hasAnyTranslation = Object.values(newTranslations).some((v) => v);
    onChange({
      ...value,
      translations: hasAnyTranslation ? newTranslations : undefined,
    });
  };

  const hasTranslations = value.translations && Object.values(value.translations).some((v) => v);

  return (
    <Box className={styles.container}>
      <TextField
        label={label}
        value={value.primary}
        onChange={(e) => handlePrimaryChange(e.target.value)}
        fullWidth
        multiline={multiline}
        rows={rows}
        variant="outlined"
        margin="normal"
        disabled={disabled}
        InputProps={{
          startAdornment: expanded ? (
            <InputAdornment position="start">
              <span className={styles.flag}>{PRIMARY_LOCALE.flag}</span>
            </InputAdornment>
          ) : undefined,
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title={expanded ? "Hide translations" : "Show translations"}>
                <IconButton
                  size="small"
                  onClick={() => setExpanded(!expanded)}
                  className={hasTranslations ? styles.hasTranslations : undefined}
                >
                  <Language fontSize="small" />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
        }}
      />
      {expanded && (
        <Box className={styles.translations}>
          {TRANSLATION_KEYS.map((locale) => {
            const config = TRANSLATION_LOCALES[locale];
            return (
              <TextField
                key={locale}
                value={value.translations?.[locale] ?? ""}
                onChange={(e) => handleTranslationChange(locale, e.target.value)}
                fullWidth
                multiline={multiline}
                rows={rows}
                variant="outlined"
                size="small"
                disabled={disabled}
                placeholder={config.label}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <span className={styles.flag}>{config.flag}</span>
                    </InputAdornment>
                  ),
                }}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
};

const styles = {
  container: css``,
  translations: css`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;
    margin-bottom: 16px;
    padding-left: 16px;
    border-left: 2px solid #e0e0e0;
  `,
  flag: css`
    font-size: 16px;
  `,
  hasTranslations: css`
    color: #1976d2;
  `,
};

export default TranslatedTextField;
